/**
 * How to read a ledger cell — the (i) affordance the KPI dashboard uses, not an
 * always-visible bar (DOP-472).
 *
 * The bar under the grid showed a sample "130.0" next to each state; on a real
 * ledger that reads as one more number. The states stay quiet by design (see
 * LedgerCell), so they need explaining once, on demand: a small button opens a
 * popover with each state's styled sample beside what it means, and closes on
 * outside click or Escape. The samples reuse the grid's own class treatments,
 * so what the popover teaches is exactly what the cells look like.
 */

import React, { useEffect, useId, useRef, useState } from "react";

const LEGEND_ITEMS: ReadonlyArray<{
	key: string;
	sample: string;
	sampleClassName: string;
	meaning: string;
}> = [
	{
		key: "found",
		sample: "130.0",
		sampleClassName: "border-b-[1.5px] border-[#004C4B]/35 text-text-base",
		meaning: "Found in a document and cited to a page. Click it to open the page.",
	},
	{
		key: "derived",
		sample: "Σ",
		sampleClassName: "text-gray-400 underline decoration-dotted underline-offset-2",
		meaning: "Calculated from other figures. Click the Σ for the formula.",
	},
	{
		key: "untied",
		sample: "130.0",
		sampleClassName:
			"text-text-base underline decoration-dotted decoration-warning-fg underline-offset-4",
		meaning: "Does not tie to the compliance certificate; worth a second look.",
	},
	{
		key: "manual",
		sample: "130.0 •",
		sampleClassName: "font-medium text-primary-600",
		meaning: "Entered by a person, with their note on record.",
	},
	{
		key: "gap",
		sample: "gap",
		sampleClassName: "bg-[#fffaeb] px-1 text-[#92400e]",
		meaning: "Not in the package. Listed on the Gaps tab as work to do.",
	},
	{
		key: "not_available",
		sample: "n/a",
		sampleClassName: "text-gray-400",
		meaning: "Accepted as not available for this borrower.",
	},
];

const MonitoringLegend: React.FC = () => {
	const [open, setOpen] = useState(false);
	const rootRef = useRef<HTMLDivElement>(null);
	const tooltipId = `${useId()}-legend`;

	// Close on outside click or Escape: a popover that only closes by clicking
	// the button again is a trap for anyone who opened it and moved on.
	useEffect(() => {
		if (!open) return undefined;
		const onPointerDown = (event: MouseEvent) => {
			if (rootRef.current && !rootRef.current.contains(event.target as Node)) {
				setOpen(false);
			}
		};
		const onKeyDown = (event: KeyboardEvent) => {
			if (event.key === "Escape") setOpen(false);
		};
		document.addEventListener("mousedown", onPointerDown);
		document.addEventListener("keydown", onKeyDown);
		return () => {
			document.removeEventListener("mousedown", onPointerDown);
			document.removeEventListener("keydown", onKeyDown);
		};
	}, [open]);

	return (
		<div ref={rootRef} className="relative shrink-0">
			<button
				type="button"
				onClick={() => setOpen((current) => !current)}
				aria-expanded={open}
				aria-describedby={open ? tooltipId : undefined}
				className="whitespace-nowrap rounded px-2 py-1 text-sm font-medium text-text-muted transition-colors hover:bg-gray-100 hover:text-text-base focus:outline-none focus:ring-1 focus:ring-[#008782]"
			>
				How to read a cell
			</button>
			{open && (
				<div
					id={tooltipId}
					role="tooltip"
					className="absolute left-0 bottom-full z-30 mb-2 w-80 rounded-md border border-gray-200 bg-white p-3 text-sm shadow-lg"
				>
					<p className="text-sm font-semibold text-text-strong">How to read a cell</p>
					<ul className="mt-2 space-y-2">
						{LEGEND_ITEMS.map((item) => (
							<li key={item.key} className="flex items-start gap-2">
								<span
									aria-hidden="true"
									className={`w-14 shrink-0 text-left tabular-nums ${item.sampleClassName}`}
								>
									{item.sample}
								</span>
								<span className="text-text-muted">{item.meaning}</span>
							</li>
						))}
					</ul>
				</div>
			)}
		</div>
	);
};

export default MonitoringLegend;
